import { z } from "zod";

/**
 * Money is always stored and transported as an integer number of MINOR units
 * (paise, cents, fils…) plus an ISO-4217 currency code. Never use floats for
 * money arithmetic. See docs/architecture/principles.md (ADR-006).
 */

/** ISO-4217 currencies we support, with their minor-unit exponent. */
export const CURRENCIES = {
  INR: { name: "Indian Rupee", symbol: "₹", exponent: 2, locale: "en-IN" },
  USD: { name: "US Dollar", symbol: "$", exponent: 2, locale: "en-US" },
  EUR: { name: "Euro", symbol: "€", exponent: 2, locale: "de-DE" },
  GBP: { name: "Pound Sterling", symbol: "£", exponent: 2, locale: "en-GB" },
  AED: { name: "UAE Dirham", symbol: "د.إ", exponent: 2, locale: "en-AE" },
  SGD: { name: "Singapore Dollar", symbol: "S$", exponent: 2, locale: "en-SG" },
  AUD: { name: "Australian Dollar", symbol: "A$", exponent: 2, locale: "en-AU" },
  CAD: { name: "Canadian Dollar", symbol: "C$", exponent: 2, locale: "en-CA" },
  SAR: { name: "Saudi Riyal", symbol: "﷼", exponent: 2, locale: "en-SA" },
  JPY: { name: "Japanese Yen", symbol: "¥", exponent: 0, locale: "ja-JP" },
  CHF: { name: "Swiss Franc", symbol: "CHF", exponent: 2, locale: "de-CH" },
  NZD: { name: "New Zealand Dollar", symbol: "NZ$", exponent: 2, locale: "en-NZ" },
  ZAR: { name: "South African Rand", symbol: "R", exponent: 2, locale: "en-ZA" },
  MYR: { name: "Malaysian Ringgit", symbol: "RM", exponent: 2, locale: "ms-MY" },
  HKD: { name: "Hong Kong Dollar", symbol: "HK$", exponent: 2, locale: "en-HK" },
  KWD: { name: "Kuwaiti Dinar", symbol: "د.ك", exponent: 3, locale: "en-KW" },
  BHD: { name: "Bahraini Dinar", symbol: ".د.ب", exponent: 3, locale: "en-BH" },
  OMR: { name: "Omani Rial", symbol: "﷼", exponent: 3, locale: "en-OM" },
  QAR: { name: "Qatari Riyal", symbol: "﷼", exponent: 2, locale: "en-QA" },
  NPR: { name: "Nepalese Rupee", symbol: "रू", exponent: 2, locale: "en-NP" },
  LKR: { name: "Sri Lankan Rupee", symbol: "Rs", exponent: 2, locale: "en-LK" },
  BDT: { name: "Bangladeshi Taka", symbol: "৳", exponent: 2, locale: "en-BD" },
} as const;

export type CurrencyCode = keyof typeof CURRENCIES;
export const CURRENCY_CODES = Object.keys(CURRENCIES) as CurrencyCode[];
export const currencyCodeSchema = z.enum(CURRENCY_CODES as [CurrencyCode, ...CurrencyCode[]]);
export const DEFAULT_CURRENCY: CurrencyCode = "INR";

/** Integer minor units. Safe up to Number.MAX_SAFE_INTEGER (~9e15 paise ≈ ₹90 trillion). */
export const minorSchema = z.number().int().safe();
export const moneySchema = z.object({ amountMinor: minorSchema, currency: currencyCodeSchema });
export type Money = z.infer<typeof moneySchema>;

export function exponentOf(currency: CurrencyCode): number {
  return CURRENCIES[currency].exponent;
}

/** Convert a decimal major-unit string/number ("1234.50") to minor units, rounding half away from zero. */
export function toMinor(major: number | string, currency: CurrencyCode): number {
  const exp = exponentOf(currency);
  const s = typeof major === "number" ? major.toFixed(exp + 2) : major.trim();
  if (!/^-?\d+(\.\d+)?$/.test(s)) throw new Error(`Invalid amount: ${major}`);
  const neg = s.startsWith("-");
  const [intPart = "0", fracPart = ""] = s.replace("-", "").split(".");
  const padded = (fracPart + "0".repeat(exp + 1)).slice(0, exp + 1);
  const base = BigInt(intPart) * 10n ** BigInt(exp) + BigInt(padded.slice(0, exp) || "0");
  const roundDigit = Number(padded[exp] ?? "0");
  const rounded = roundDigit >= 5 ? base + 1n : base;
  const result = Number(neg ? -rounded : rounded);
  if (!Number.isSafeInteger(result)) throw new Error("Amount out of range");
  return result;
}

/** Minor units → major-unit decimal string ("1234.50"). Exact, no float error. */
export function toMajorString(minor: number, currency: CurrencyCode): string {
  const exp = exponentOf(currency);
  const neg = minor < 0;
  const abs = BigInt(Math.abs(minor));
  if (exp === 0) return `${neg ? "-" : ""}${abs}`;
  const div = 10n ** BigInt(exp);
  return `${neg ? "-" : ""}${abs / div}.${(abs % div).toString().padStart(exp, "0")}`;
}

/** Locale-aware display, e.g. ₹1,23,456.50 for INR (Indian digit grouping). */
export function formatMoney(
  minor: number,
  currency: CurrencyCode,
  opts: { compact?: boolean; locale?: string; hideZeroDecimals?: boolean } = {},
): string {
  const exp = exponentOf(currency);
  const major = Number(toMajorString(minor, currency));
  const locale = opts.locale ?? CURRENCIES[currency].locale;
  const fractionDigits = opts.hideZeroDecimals && minor % 10 ** exp === 0 ? 0 : exp;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    notation: opts.compact ? "compact" : "standard",
    minimumFractionDigits: opts.compact ? 0 : fractionDigits,
    maximumFractionDigits: opts.compact ? 1 : fractionDigits,
  }).format(major);
}

/**
 * Allocate an amount across N parts proportionally to integer weights without
 * losing or creating a single minor unit (largest-remainder method).
 */
export function allocate(totalMinor: number, weights: number[]): number[] {
  const sum = weights.reduce((a, b) => a + b, 0);
  if (sum === 0) return weights.map(() => 0);
  const raw = weights.map((w) => (totalMinor * w) / sum);
  const floored = raw.map((r) => Math.floor(r));
  let remainder = totalMinor - floored.reduce((a, b) => a + b, 0);
  const order = raw.map((r, i) => ({ i, frac: r - Math.floor(r) })).sort((a, b) => b.frac - a.frac);
  for (const { i } of order) {
    if (remainder <= 0) break;
    floored[i]! += 1;
    remainder -= 1;
  }
  return floored;
}

/** Percentage of an amount in basis points (1% = 100 bps), rounded half-up. */
export function applyBps(amountMinor: number, bps: number): number {
  return Math.round((amountMinor * bps) / 10_000);
}

/**
 * Convert between currencies using a rate expressed as a decimal string with up
 * to 10 fractional digits ("83.1234567890" = 1 unit of FROM in TO). Uses BigInt
 * fixed-point so large amounts do not lose precision.
 */
export function convertMinor(
  amountMinor: number,
  from: CurrencyCode,
  to: CurrencyCode,
  rate: string,
): number {
  if (from === to) return amountMinor;
  const SCALE = 10;
  const [i = "0", f = ""] = rate.split(".");
  const rateScaled = BigInt(i + (f + "0".repeat(SCALE)).slice(0, SCALE));
  const expDiff = exponentOf(to) - exponentOf(from);
  let num = BigInt(amountMinor) * rateScaled;
  let den = 10n ** BigInt(SCALE);
  if (expDiff > 0) num *= 10n ** BigInt(expDiff);
  if (expDiff < 0) den *= 10n ** BigInt(-expDiff);
  const neg = num < 0n;
  const absNum = neg ? -num : num;
  const q = absNum / den;
  const r = absNum % den;
  const rounded = r * 2n >= den ? q + 1n : q;
  return Number(neg ? -rounded : rounded);
}
